import { useCallback } from 'react';

import { AttendeeHubPageType } from '@graphql/gql/graphql';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import useMeQuery from '@hooks/useMeQuery';
import useAppFeatures from '@hooks/useAppFeatures';
import { NotificationLinkedContentPageType } from './utils';

interface UseLinkedContentVisiblity {
  isLinkedContentVisible: (type: string, id: string) => boolean;
}

const getFeatureSettingsName = (type: string) => {
  switch (type) {
    case NotificationLinkedContentPageType.ATTENDEE_CONNECTIONS:
      return AttendeeHubPageType.AttendeeConnections;
    case NotificationLinkedContentPageType.SCHEDULE:
      return 'MY_SCHEDULE';
    case NotificationLinkedContentPageType.MESSAGES:
      return 'MESSAGING';
    case NotificationLinkedContentPageType.ATTENDEE_PROFILE:
      return AttendeeHubPageType.AttendeeProfile;
    case NotificationLinkedContentPageType.ATTENDEE_LIST:
      return AttendeeHubPageType.AttendeeList;
    case NotificationLinkedContentPageType.SPEED_NETWORKING:
      return AttendeeHubPageType.SpeedNetworking;
    case NotificationLinkedContentPageType.UPCOMING_EVENTS:
      return AttendeeHubPageType.UpcomingEvents;
    case NotificationLinkedContentPageType.YOUR_EVENTS:
      return AttendeeHubPageType.YourEvents;
    default:
      return type;
  }
};

const useLinkedContentVisiblity = (): UseLinkedContentVisiblity => {
  const { internalAHLinkingRedesign } = useAppFeatures();
  const { data: dataFromMe } = useMeQuery();
  const { event } = dataFromMe?.me || {};
  const { isFeatureSettingEnabled, customPageSettings } = useAttendeeHubConfig();
  const isCommunityPageEnabled =
    isFeatureSettingEnabled('ATTENDEE_LIST') && isFeatureSettingEnabled('VIDEO_DISCUSSIONS');

  // get content visibility settings
  const isSponsoredExhibitorsEnabled =
    event?.features?.sponsors && event?.features?.showSponsorList && isFeatureSettingEnabled('EXHIBITOR_LIST');
  const isExhibitorListVisible =
    event?.features?.exhibitorManagement &&
    event?.features?.showExhibitorList &&
    event?.features?.exhibitorsPresent &&
    isFeatureSettingEnabled('EXHIBITOR_LIST');
  const isDiscussionsEnabled = isFeatureSettingEnabled('VIDEO_DISCUSSIONS');
  const isOnDemandVisible = event?.features?.onDemandCatalog;
  const isSessionsVisible = event?.features?.sessions;
  const isSpeakersVisible = event?.features?.speakers;
  const isFloorPlanVisible = event?.features?.floorPlans;
  const isGamePresent = event?.features?.gamePresent;
  const isGamificationEnabled = isFeatureSettingEnabled('GAME');
  const surveyLink = event?.links?.surveys;
  const isSurveysVisible = !!surveyLink;

  const isLinkedContentVisible = useCallback(
    (type: string, id: string) => {
      if (!internalAHLinkingRedesign) {
        return true;
      }
      let canBeShown = true;
      switch (type) {
        case NotificationLinkedContentPageType.EXHIBITORS:
        case NotificationLinkedContentPageType.EXHIBITORS_LIST:
          canBeShown = isExhibitorListVisible;
          break;
        case NotificationLinkedContentPageType.SPONSORS_LIST:
          canBeShown = !!isSponsoredExhibitorsEnabled;
          break;
        case NotificationLinkedContentPageType.SESSIONS:
        case NotificationLinkedContentPageType.SESSIONS_LIST:
          canBeShown = isSessionsVisible;
          break;
        case NotificationLinkedContentPageType.SPEAKERS:
        case NotificationLinkedContentPageType.SPEAKERS_LIST:
          canBeShown = isSpeakersVisible;
          break;
        case NotificationLinkedContentPageType.DISCUSSIONS:
        case NotificationLinkedContentPageType.DISCUSSIONS_LIST:
          canBeShown = isDiscussionsEnabled;
          break;
        case NotificationLinkedContentPageType.ON_DEMAND_CONTENT_LIBRARY:
          canBeShown = isOnDemandVisible;
          break;
        case NotificationLinkedContentPageType.FLOOR_PLAN:
        case NotificationLinkedContentPageType.FLOOR_PLANS_LIST:
          canBeShown = isFloorPlanVisible;
          break;
        case NotificationLinkedContentPageType.SURVEYS:
          canBeShown = isSurveysVisible;
          break;
        case NotificationLinkedContentPageType.COMMUNITY:
          canBeShown = isCommunityPageEnabled;
          break;
        case NotificationLinkedContentPageType.GAME:
          canBeShown = isGamificationEnabled && isGamePresent;
          break;
        case NotificationLinkedContentPageType.HOME:
          canBeShown = true;
          break;
        case NotificationLinkedContentPageType.CUSTOM_PAGES:
        case NotificationLinkedContentPageType.DOCUMENT_PAGES:
        case NotificationLinkedContentPageType.EXTERNAL_PAGES:
          canBeShown = customPageSettings.some(customPage => customPage.id === id);
          break;
        case NotificationLinkedContentPageType.PROFILE_MENU:
        case NotificationLinkedContentPageType.TRAVEL_DETAILS:
        case NotificationLinkedContentPageType.QR_CODE:
          canBeShown = false;
          break;
        default: {
          // Check to see if feature is enabled for pages:
          // Attendee List , Schedule, Text and Video Discussions, Game, Your Events, Attendee Profile, Attendee Connections and Speed Networking
          if (getFeatureSettingsName(type) !== type) {
            canBeShown = isFeatureSettingEnabled(getFeatureSettingsName(type));
          }
        }
      }
      return canBeShown;
    },
    [
      customPageSettings,
      isExhibitorListVisible,
      isFeatureSettingEnabled,
      isOnDemandVisible,
      isSessionsVisible,
      isSponsoredExhibitorsEnabled,
      isCommunityPageEnabled,
      internalAHLinkingRedesign,
      isSurveysVisible
    ]
  );

  return {
    isLinkedContentVisible
  };
};

export default useLinkedContentVisiblity;
